import _includes from 'lodash/includes';
import _sortBy from 'lodash/sortBy';
import { YesNoEnum } from 'src/services/jmr/types';
import { MeasureUnitAbbreviations, MeasureUnitValues } from 'src/services/tasks/constants';
import type { RegionTask, RegionTaskGroup, RegionTasksAndGroupsDTO } from 'src/services/tasks/types';
import type { BackgroundFormData, JmrTask } from 'src/store/newJmrPageSlice/types';
import { type IdentifyBodyLocationEnum } from 'src/utils/jmrUtils';

import { isEmpty, isNotEmpty } from './helpers';

// ***** Constants **** //

const ValidationErrors = {
  REQUIRED_VALUE_MISSING: 'A value must be selected',
  NEXT_RANGE_VALUE_TOO_HIGH:
    'Task of a higher maximum weight range cannot have greater hours than a task with a lower maximum range',
} as const;

// **** Types **** //

export type RestrictionOption = RestrictionTask | RestrictionTaskGroup;

// Restriction Task Options for the dropdown selectors
export interface FormattedTaskOption {
  value: RestrictionOption['entityId'];
  label: RestrictionOption['label'];
  key: RestrictionOption['entityId'];
}

export type AllRestrictionTypes =
  | RestrictionTask
  | RestrictionTaskGroup
  | TimeRestrictionTask
  | YesNoRestrictionTask
  | WeightRestrictionTaskGroup
  | WeightRestrictionTask;

export interface RestrictionTask extends RegionTask {
  type: 'restriction-task';
  entityId: `t-${number}`;
  restrictedValue?: string;
  bodyLocationValue?: IdentifyBodyLocationEnum | null;
  error?: string;
}

export interface RestrictionTaskGroup extends Omit<RegionTaskGroup, 'tasks'> {
  type: 'restriction-task-group';
  entityId: `g-${number}`;
  restrictionTasks: RestrictionTask[];
  bodyLocationValue?: IdentifyBodyLocationEnum | null;
  error?: string;
}

export interface TimeRestrictionTask extends Omit<RestrictionTask, 'measureUnit' | 'restrictedValue'> {
  measureUnit: MeasureUnitValues.Hours;
  restrictedValue: string | undefined;
}

export interface YesNoRestrictionTask extends Omit<RestrictionTask, 'measureUnit' | 'restrictedValue'> {
  measureUnit: MeasureUnitValues.Bool;
  restrictedValue: 'No';
}

export interface WeightRestrictionTaskGroup extends Omit<RestrictionTaskGroup, 'measureUnit' | 'restrictionTasks'> {
  measureUnit: MeasureUnitValues.Hours;
  restrictionTasks: WeightRestrictionTask[];
  restrictionMaxUnit: MeasureUnitValues.Pounds | MeasureUnitValues.Kilograms;
  showChildSelectors: boolean;
  maxWeightRange: number | undefined;
}

export interface WeightRestrictionTask
  extends Omit<RestrictionTask, 'measureUnit' | 'restrictionMaxUnit' | 'restrictionMaxValue'> {
  measureUnit: MeasureUnitValues.Hours;
  restrictionMaxUnit: MeasureUnitValues.Pounds | MeasureUnitValues.Kilograms;
  restrictionMaxValue: number;
  showSelectorEle: boolean;
}

export interface RestrictionReviewItem {
  label: string;
  value: string | null | undefined;
}

// **** Functions **** //

/**
 * Validator: check arg is "RestrictionTask"
 */
export const isRestrictionTask = (arg: RestrictionTask | RestrictionTaskGroup): arg is RestrictionTask =>
  arg.type === 'restriction-task';

/**
 * Validator: check arg is "RestrictionTask"
 */
export const isRestrictionTaskGroup = (arg: RestrictionTask | RestrictionTaskGroup): arg is RestrictionTaskGroup =>
  arg.type === 'restriction-task-group';

/**
 * Is a TimeRestrictionTask
 */
export const isTimeRestrictionTask = (arg: RestrictionTask | RestrictionTaskGroup): arg is TimeRestrictionTask =>
  isRestrictionTask(arg) && arg.measureUnit === MeasureUnitValues.Hours;

/**
 * Is a TimeRestrictionTask
 */
export const isYesNoRestrictionTask = (arg: RestrictionTask | RestrictionTaskGroup): arg is YesNoRestrictionTask =>
  isRestrictionTask(arg) && arg.measureUnit === MeasureUnitValues.Bool;

/**
 * Check if an object is a weight
 */
const isWeightMeasuredItem = (arg: {
  measureUnit: MeasureUnitValues;
  restrictionMaxUnit: MeasureUnitValues | null;
}) => {
  const { Hours, Pounds, Kilograms } = MeasureUnitValues;
  const { measureUnit, restrictionMaxUnit } = arg;
  return measureUnit === Hours && _includes([Pounds, Kilograms], restrictionMaxUnit);
};

/**
 * Check if a weight-restriction task-group.
 */
export const isWeightRestrictionTask = (arg: RestrictionTask | RestrictionTaskGroup): arg is WeightRestrictionTask =>
  isRestrictionTask(arg) && isWeightMeasuredItem(arg) && arg.restrictionMaxValue !== null;

/**
 * Check if a weight-restriction task-group.
 */
export const isWeightRestrictionTaskGroup = (
  arg: RestrictionTask | RestrictionTaskGroup,
): arg is WeightRestrictionTaskGroup =>
  isRestrictionTaskGroup(arg) && 'maxWeightRange' in arg && 'showChildSelectors' in arg;

/**
 * Make sure a child weight-restriction task is valid. If not, move it to the ungrouped tasks.
 */
const filterInvalidMemberTask = (childTask: RegionTask, ungroupedTasks: RegionTask[]): boolean => {
  if (!!childTask.restrictionMaxValue && !isNaN(childTask.restrictionMaxValue)) {
    return true;
  }
  ungroupedTasks.push(childTask);
  return false;
};

/**
 * For the incoming data for regionGroupsAndTasks, format the weight-measured items, and set default
 * values.
 *
 * Some notes on default values:
 * - Weight-measured groups: 'maxWeightRange' set here
 * - Weight-measured child-tasks: 'restrictedValue' based on shift length so has to be set dynamically
 *  see @initWeightRestrictionTaskGroupChildren
 * - Time-measured tasks: 'restrictedValue' set here
 * - YesNo tasks: legacy code sets the value in outgoing payload, but I'm doing it here to
 *  keep the logic organized.
 */
export const regionTasksAndGroupsPayloadToRestrictionOptionsList = (
  payload: RegionTasksAndGroupsDTO,
): RestrictionOption[] => {
  const { taskGroups, ungroupedTasks } = payload;
  // Initialize 'RestrictionTaskGroups'
  let restrictionTaskGroups: RestrictionTaskGroup[] = taskGroups.map(({ tasks, ...group }) => ({
    ...group,
    type: 'restriction-task-group',
    entityId: `g-${group.taskGroupId}`,
    restrictionTasks: tasks.map((task) => ({
      ...task,
      type: 'restriction-task',
      entityId: `t-${task.taskId}`,
    })),
  }));
  // Initialize 'WeightRestrictionTaskGroups' and 'WeightRestrictionTasks'
  restrictionTaskGroups = restrictionTaskGroups.map((item) => {
    const { restrictionTasks, ...group } = item;
    if (!restrictionTasks.some(isWeightRestrictionTask)) return item;
    return {
      ...group,
      maxWeightRange: undefined,
      showChildSelectors: false,
      restrictionTasks: restrictionTasks
        .filter((task) => filterInvalidMemberTask(task, ungroupedTasks))
        .map((task) => ({ ...task, showSelectorEle: false })),
    };
  });
  // Initialze 'RestrictionTasks'
  const restrictionTasks: RestrictionTask[] = ungroupedTasks.map((task) => {
    let defaultValue = {};
    if (task.measureUnit === MeasureUnitValues.Hours) {
      defaultValue = { restrictedValue: undefined };
    } else if (task.measureUnit === MeasureUnitValues.Bool) {
      defaultValue = { restrictedValue: YesNoEnum.No };
    }
    return {
      ...task,
      type: 'restriction-task',
      entityId: `t-${task.taskId}`,
      ...defaultValue,
    };
  });
  // Sort and return full list
  const allRestrictions = [...restrictionTaskGroups, ...restrictionTasks];
  return _sortBy(allRestrictions, 'rank');
};

/**
 * Determine the shift-length from the "backgroundFormData."
 */
export const getEffectiveShiftLength = (backgroundFormData: BackgroundFormData): number => {
  const shiftLength = backgroundFormData.cannotWorkNormalShiftHrs
    ? backgroundFormData.limitedHoursPerDay
    : backgroundFormData.shiftLength;
  return Number(shiftLength);
};

/**
 * Get the label for a WeightRestrictionTask. Need this in the restrictions AND review page.
 */
export const getWeightRestrictionTaskLabel = (task: WeightRestrictionTask) =>
  `Up to ${task.restrictionMaxValue} ${MeasureUnitAbbreviations[task.restrictionMaxUnit]}`;

/**
 * Show a child selector if the restrictionMaxValue is less than the parent.
 */
const getShouldShowChildWeightSelector = (child: WeightRestrictionTask, group: WeightRestrictionTaskGroup): boolean => {
  const { maxWeightRange } = group;
  if (maxWeightRange !== undefined) {
    return child.restrictionMaxValue <= maxWeightRange;
  }
  throw new Error('Parent should have restrictionMaxValue set when determining to show a child selector.');
};

/**
 * Show a child selector if the restrictionMaxValue is less than the parent.
 */
const getChildWeightSelectorDefaultValue = (
  child: WeightRestrictionTask,
  group: WeightRestrictionTaskGroup,
  shiftLength: string,
): string => {
  const { maxWeightRange } = group;
  if (maxWeightRange !== undefined) {
    return child.restrictionMaxValue > maxWeightRange ? '0' : shiftLength;
  }
  throw new Error('Parent should have restrictionMaxValue set when determining child default value.');
};

/**
 * For child tasks under the max range, allow users to set the hours. If over the max range,
 * we just leave them as '0' cause the worker can't do them at all.
 */
export const initWeightRestrictionTaskGroupChildren = (
  taskGroup: WeightRestrictionTaskGroup,
  shiftLength: string,
): WeightRestrictionTaskGroup['restrictionTasks'] =>
  taskGroup.restrictionTasks.map((child) => {
    const { error, ...cleanChild } = child;
    return {
      ...cleanChild,
      restrictedValue: getChildWeightSelectorDefaultValue(child, taskGroup, shiftLength),
      showSelectorEle: getShouldShowChildWeightSelector(child, taskGroup),
    };
  });

/**
 * Validate the children. A higher ranked child's restricted value cannot be greater
 * than a lower ranked child's restricted value.
 */
export const validateWeightRestrictionTaskGroupChildren = (
  taskGroup: WeightRestrictionTaskGroup,
): WeightRestrictionTaskGroup => {
  const restrictionTasks: WeightRestrictionTask[] = taskGroup.restrictionTasks.map(({ error, ...c }) => c);
  let currentMin = Number(restrictionTasks[0].restrictedValue ?? Number.MAX_SAFE_INTEGER);
  for (let i = 1; i < restrictionTasks.length; i++) {
    const child = restrictionTasks[i];
    const currentValue = Number(child.restrictedValue ?? Number.MAX_SAFE_INTEGER);
    if (currentValue > currentMin) {
      restrictionTasks[i] = { ...child, error: ValidationErrors.NEXT_RANGE_VALUE_TOO_HIGH };
    }
    currentMin = Math.min(currentValue, currentMin);
  }
  return { ...taskGroup, restrictionTasks };
};

/**
 * Validate the list of restriction items
 */
export const validateSelectedRestrictionOptions = (list: RestrictionOption[]) => {
  let isValid = true;
  const updatedList = list.map((item) => {
    if (isWeightRestrictionTaskGroup(item)) {
      if (item.maxWeightRange === undefined) {
        isValid = false;
        return { ...item, error: ValidationErrors.REQUIRED_VALUE_MISSING };
      }
      const hasChildError = item.restrictionTasks.some((c) => !!c.error);
      if (hasChildError) isValid = false;
    } else if (isTimeRestrictionTask(item)) {
      if (item.restrictedValue === undefined) {
        isValid = false;
        return { ...item, error: ValidationErrors.REQUIRED_VALUE_MISSING };
      }
    }
    return item;
  });
  return { updatedList, isValid };
};

/**
 * @see selectedRestrictionOptionsToJmrTaskList
 *
 * RestrictionTask -> JmrTask
 */
const restrictionTaskToJmrTask = (task: RestrictionTask): JmrTask => ({
  regionId: task.regionId,
  taskId: task.taskId,
  rank: task.rank,
  label: task.label,
  description: task.description,
  identifyBodyLocation: task.identifyBodyLocation,
  measureUnit: task.measureUnit,
  restrictionMaxUnit: task.restrictionMaxUnit,
  restrictionMaxValue: task.restrictionMaxValue,
  restrictedValue: task.restrictedValue ?? '',
  ...(task.bodyLocationValue
    ? {
        bodyLocationValue: task.bodyLocationValue,
      }
    : {}),
});

/**
 * Convert a list of Restrictions to JmrTasks. Useful when going forward from the
 * restrictions page.
 *
 * (RestrictionTask | RestrictionTaskGroup)[] -> JmrTask[]
 */
export const selectedRestrictionOptionsToJmrTaskList = (
  selectedOptions: RestrictionOption[],
  shiftLength: string,
): JmrTask[] =>
  selectedOptions.reduce<JmrTask[]>((accu, task) => {
    if (isRestrictionTaskGroup(task)) {
      if (!isWeightRestrictionTaskGroup(task)) return accu;
      const children = selectedRestrictionOptionsToJmrTaskList(task.restrictionTasks, shiftLength);
      return [...accu, ...children];
    } else if (
      isEmpty(task.restrictedValue) ||
      // If the restrictedValue is the shiftlength, then this task is not considered a
      // restriction and should not factor into the job match result logic.
      (isWeightRestrictionTask(task) && task.restrictedValue === shiftLength)
    ) {
      return accu;
    } else {
      const jmrTask = restrictionTaskToJmrTask(task);
      return [...accu, jmrTask];
    }
  }, []);

/**
 * Look an incoming list of selected-jmr-tasks and setup the selected-restriction-list items accordingly.
 * Useful when updating a JMR and we have to start with a list of JMR tasks.
 */
export const setupPreselectedRestrictionOptions = (
  jmrTasks: JmrTask[],
  restrictionTaskOptions: RestrictionOption[],
  shiftLength: number,
): RestrictionOption[] => {
  // Initialize values
  const jmrTaskMap = new Map(jmrTasks.map((t) => [t.taskId, t]));
  const selectedOptionsMap = new Map<number, RestrictionOption>();
  restrictionTaskOptions.forEach((restrictionTaskOption) => {
    // Setup 'weighted task-groups'
    if (isWeightRestrictionTaskGroup(restrictionTaskOption)) {
      const { restrictionTasks } = restrictionTaskOption;
      const hasChildWhichIsAJmrTask = restrictionTasks.some((c) => jmrTaskMap.get(c.taskId));
      if (hasChildWhichIsAJmrTask) {
        const selectedGroup = { ...restrictionTaskOption };
        // Initialize the child values
        selectedGroup.restrictionTasks = restrictionTasks.map((restrictionTask) => {
          const selectedChild = { ...restrictionTask };
          const childJmrTask = jmrTaskMap.get(selectedChild.taskId);
          selectedChild.restrictedValue = childJmrTask?.restrictedValue ?? String(shiftLength);
          if (childJmrTask?.bodyLocationValue) {
            selectedChild.bodyLocationValue = childJmrTask?.bodyLocationValue;
            selectedGroup.bodyLocationValue = childJmrTask?.bodyLocationValue;
          }
          if (isNotEmpty(selectedChild.restrictedValue)) {
            const rValue = Number(selectedChild.restrictedValue);
            if (rValue > 0 && rValue < shiftLength) {
              selectedGroup.showChildSelectors = true;
            }
          }
          selectedOptionsMap.set(selectedChild.taskId, selectedGroup); // Map the child to the parent
          return selectedChild;
        });
        // Set the max-weight-range
        const nonRestrictedChildren = selectedGroup.restrictionTasks.filter((c) => c.restrictedValue !== '0');
        const lastNonRestrictedChild = nonRestrictedChildren.slice(-1)[0];
        selectedGroup.maxWeightRange = lastNonRestrictedChild?.restrictionMaxValue ?? 0;
        selectedGroup.restrictionTasks.forEach((child) => {
          child.showSelectorEle = getShouldShowChildWeightSelector(child, selectedGroup);
        });
      }
      // Setup all other tasks
    } else if (isRestrictionTask(restrictionTaskOption)) {
      const jmrTask = jmrTaskMap.get(restrictionTaskOption.taskId);
      if (!!jmrTask) {
        const { restrictedValue, bodyLocationValue } = jmrTask;
        const selectedTask = {
          ...restrictionTaskOption,
          ...(restrictedValue ? { restrictedValue } : {}),
          ...(bodyLocationValue ? { bodyLocationValue } : {}),
        };
        selectedOptionsMap.set(selectedTask.taskId, selectedTask);
      }
    }
  });
  // Order the selectedRestrictionOptions to match the preSelectedTasks. The
  // tracker is so we don't add the parent twice.
  const tracker = new Set<string>();
  return jmrTasks.reduce<RestrictionOption[]>((accu, jmrTask) => {
    const selectedOption = selectedOptionsMap.get(jmrTask.taskId);
    if (selectedOption && !tracker.has(selectedOption.entityId)) {
      tracker.add(selectedOption.entityId);
      return [...accu, selectedOption];
    } else {
      return accu;
    }
  }, []);
};

/**
 * Get the restricted value appended with the abbreviation for measureUnit
 * i.e. => '20 lbs'
 */
const getRestrictedValueWithUnit = (option: RestrictionTask, shiftLength?: number): string => {
  const { restrictedValue, measureUnit } = option;
  const value = isEmpty(restrictedValue) && isNotEmpty(shiftLength) ? shiftLength : restrictedValue;
  return `${value} ${MeasureUnitAbbreviations[measureUnit]}`;
};

/**
 * Setup the objects to dispay based on the Task type.
 */
export const setupRestrictionReviewItems = (item: RestrictionOption, shiftLength: number): RestrictionReviewItem[] => {
  // Add the label
  let retVal: { label: string; value: string | null | undefined }[] = [
    {
      label: 'Task',
      value: item.label,
    },
  ];
  // Add the body location value
  if (item.identifyBodyLocation) {
    retVal.push({
      label: 'Body Location',
      value: item.bodyLocationValue,
    });
  }
  // If weight restriction parent task
  if (isWeightRestrictionTaskGroup(item)) {
    retVal.push({
      label: 'Maximum Weight',
      value: `${item.maxWeightRange} ${MeasureUnitAbbreviations[item.restrictionMaxUnit]}`,
    });
    item.restrictionTasks.forEach((childTask) => {
      if (getShouldShowChildWeightSelector(childTask, item)) {
        retVal.push({
          label: getWeightRestrictionTaskLabel(childTask),
          value: getRestrictedValueWithUnit(childTask, shiftLength),
        });
      }
    });
    // Is time restriction task
  } else if (isTimeRestrictionTask(item)) {
    retVal.push({
      label: 'Maximum Time',
      value: getRestrictedValueWithUnit(item),
    });
    // YES/NO restriction task
  } else if (isYesNoRestrictionTask(item)) {
    retVal = [
      {
        label: 'Task',
        value: `${item.label} - NO`,
      },
    ];
  }
  // Return
  return retVal;
};
